import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, MessageSquareHeart, Inbox as InboxIcon, Gauge, Monitor } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = [
  { to: "/app/m", end: true, icon: LayoutDashboard, label: "Home", testid: "m-nav-home" },
  { to: "/app/m/engage", icon: MessageSquareHeart, label: "Engage", testid: "m-nav-engage", badge: "engage" },
  { to: "/app/m/inbox", icon: InboxIcon, label: "Inbox", testid: "m-nav-inbox", badge: "inbox" },
  { to: "/app/m/budget", icon: Gauge, label: "Budget", testid: "m-nav-budget" },
];

export default function MobileApp() {
  const { workspace } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({ inbox: 0, engage: 0 });
  const [killActive, setKillActive] = useState(false);

  const loadBadges = async () => {
    try {
      const [nr, eng, ks] = await Promise.all([
        api.get("/m365/inbox/needs-reply-count").catch(() => ({ data: {} })),
        api.get("/linkedin-engage/engagements?status=pending_review").catch(() => ({ data: {} })),
        api.get("/system/kill-switch").catch(() => ({ data: {} })),
      ]);
      setCounts({
        inbox: nr.data?.count || 0,
        engage: (eng.data?.engagements || []).length,
      });
      setKillActive(!!ks.data?.active);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadBadges(); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="mobile-app">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <div className="flex items-center gap-2">
          <img src="/pwa-icon-192.png" alt="LogiGrowth" className="w-7 h-7 rounded-md" />
          <div className="leading-tight">
            <div className="text-sm font-semibold">LogiGrowth</div>
            <div className="text-[0.6rem] text-muted-foreground truncate max-w-[150px]">{workspace?.name || "Command"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {killActive && (
            <span className="text-[0.6rem] font-semibold text-red-400 border border-red-500/40 bg-red-500/10 px-2 py-0.5 rounded-full" data-testid="m-kill-indicator">
              AI PAUSED
            </span>
          )}
          <Link to="/app" className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-full px-2.5 py-1.5 text-muted-foreground active:text-foreground" title="Open full desktop app" data-testid="m-open-desktop">
            <Monitor className="w-3.5 h-3.5" />
            Desktop
          </Link>
        </div>
      </header>

      {/* Page */}
      <main className="flex-1 px-4 py-4 pb-28 max-w-xl w-full mx-auto">
        <Outlet context={{ reloadBadges: loadBadges }} />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-[10000] bg-card/95 backdrop-blur border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
        <div className="max-w-xl mx-auto grid grid-cols-4">
          {TABS.map((t) => {
            const badge = t.badge ? counts[t.badge] : 0;
            return (
              <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
                className={({ isActive }) =>
                  `relative flex flex-col items-center gap-1 py-2.5 text-[0.65rem] transition-colors ${isActive ? "text-signal-500" : "text-muted-foreground"}`}>
                <t.icon className="w-5 h-5" />
                <span>{t.label}</span>
                {badge > 0 && (
                  <span className="absolute top-1.5 right-[22%] min-w-[16px] h-4 px-1 rounded-full bg-signal-500 text-background text-[0.6rem] font-bold flex items-center justify-center">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
